import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, WebApplicationSchema } from '@/components/seo/seo';
import { DFT_ENTITY } from '@/data/council-entities';

// Mac's capabilities
const capabilities = [
  {
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
    title: 'Photo Analysis',
    description: "I examine your damage photos and identify key evidence. I'll spot what's strong and flag what might need another shot.",
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
    title: 'Document Review',
    description: "I check your paperwork for completeness and consistency. Missing something? I'll let you know before it becomes a problem.",
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
      </svg>
    ),
    title: 'Evidence Building',
    description: 'I search available council transparency data and prior-report sources for relevant context to include in your pack.',
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
      </svg>
    ),
    title: 'Note Refinement',
    description: 'I help turn your account of what happened into clear, professional language for you to review.',
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
      </svg>
    ),
    title: 'Claim Preparation',
    description: "I pull everything together into a professional claim pack. Organised, properly formatted, ready to submit.",
  },
];

// Machine-readable capability list for the WebApplication schema —
// third-person equivalents of the capability cards above
const featureList = [
  'Pothole and vehicle damage photo analysis with evidence quality feedback',
  'Evidence document review for completeness and consistency',
  'Automated identification of the responsible highway authority (council, National Highways, or TfL) with its DfT road maintenance rating',
  'Prior pothole report discovery using FixMyStreet and council transparency data',
  'Incident description refinement into clear, professional language',
  'Professional claim pack assembly, organised and ready to submit',
];

// FAQs — rendered visibly and emitted as FAQPage JSON-LD
const faqs = [
  {
    question: 'Is Mac a real person?',
    answer:
      "No. Mac is Fixtyer's AI document-preparation assistant — software designed to analyse pothole claim evidence. The friendly persona makes the process approachable, but Fixtyer is upfront that Mac is AI, not a human.",
  },
  {
    question: 'What exactly can Mac do?',
    answer:
      'Mac analyses your pothole and vehicle damage photos, checks documents for completeness, identifies the responsible highway authority from your incident location along with its DfT road maintenance rating, searches FixMyStreet and council transparency data for prior reports of the same defect, refines your incident description into professional language, and assembles everything into a ready-to-submit claim pack.',
  },
  {
    question: 'Does Mac give legal advice?',
    answer:
      'No. Mac is not a solicitor and does not provide legal advice. It helps you prepare documents and organise evidence. If your case is complex or becomes a legal dispute, speak to a qualified solicitor.',
  },
  {
    question: 'Can Mac guarantee my claim will succeed?',
    answer:
      "No. The outcome depends on your evidence and the authority's assessment. Mac helps organise your evidence and prepare clear documents. DfT ratings and prior reports can provide context, but do not determine liability or automatically defeat a Section 58 defence.",
  },
  {
    question: 'Does Mac submit my claim to the council?',
    answer:
      'No. Fixtyer prepares the claim pack and you submit it yourself, directly to the council. You keep 100% of any compensation — Fixtyer charges a one-off fee for the pack, not a cut of your payout.',
  },
  {
    question: 'How does Mac know what councils look for?',
    answer:
      'Mac uses Fixtyer templates maintained with reference to the Highways Act 1980, published council processes, Department for Transport road maintenance data, and other public sources. This is general information, not legal advice.',
  },
];

// How Mac works — concrete processing steps in Mac's voice
const howItWorks = [
  {
    title: 'I check your photos for useful detail',
    description:
      "When you upload photos, I check they actually demonstrate what a council will look for: the depth and edges of the pothole, something for scale, and damage — tyre, wheel, suspension, or bodywork — that's consistent with the incident you've described. If a shot is too weak to carry its weight, I'll tell you exactly what to retake.",
  },
  {
    title: 'I identify who is responsible for the road',
    description:
      "From your incident location, I work out the responsible highway authority — your local council, National Highways for trunk roads and motorways, or TfL for London's red routes — and pull up its official Department for Transport road maintenance rating.",
  },
  {
    title: 'I check whether the council had prior notice',
    description:
      'I search historical road defect reports on FixMyStreet for earlier reports of the same pothole. A relevant prior report may help establish context about notice, but its significance depends on the specific facts and evidence.',
  },
  {
    title: 'I explain common council responses',
    description:
      'I explain common issues raised in published council claim processes and help organise the evidence you provide. I do not predict the authority’s response or the outcome.',
  },
  {
    title: 'I add relevant public context',
    description:
      'Councils publish road-condition, spending, and maintenance information. I can include relevant source data as context, but network-level data is not proof of liability for a specific incident.',
  },
];

export function MeetMacPage() {
  const breadcrumbs = [
    { name: 'Home', url: '/' },
    { name: 'Meet Mac', url: '/meet-mac' },
  ];

  return (
    <div className="relative">
      <SEO
        title="Meet Mac | Your Pothole Claims Assistant | Fixtyer"
        description="Mac is Fixtyer's AI-powered document-preparation assistant. See how Mac helps organise evidence and prepare a pothole damage claim pack for your review."
        canonical="/meet-mac"
      />
      <BreadcrumbSchema items={breadcrumbs} />
      <WebApplicationSchema
        name="Mac — Fixtyer AI Claims Assistant"
        alternateName="Mac"
        description="AI-powered assistant that helps UK motorists prepare pothole damage claim packs: photo analysis, document review, prior report discovery, description refinement, and document assembly."
        url="/meet-mac"
        featureList={featureList}
        offerPrice="35.00"
        offerDescription="Council Claim Pack prepared with Mac's analysis"
        about={[
          {
            '@type': 'Legislation',
            name: 'Highways Act 1980',
            url: 'https://www.legislation.gov.uk/ukpga/1980/66',
          },
          {
            '@type': 'GovernmentOrganization',
            name: DFT_ENTITY.name,
            url: DFT_ENTITY.url,
            sameAs: [DFT_ENTITY.wikipedia, DFT_ENTITY.wikidata],
          },
        ]}
      />
      <FAQSchema faqs={faqs} />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-brand-50 via-white to-accent-50 py-16 lg:py-24">
        {/* Top diagonal stripe bar */}
        <div 
          className="absolute top-0 left-0 right-0 h-8"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`
          }}
        />

        {/* Background pattern */}
        <div className="absolute inset-0 opacity-30 mt-8">
          <svg className="absolute w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="currentColor" strokeWidth="0.5" className="text-brand-200" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>

        {/* Floating decorative elements */}
        <div className="absolute top-32 left-10 w-20 h-20 bg-accent-500/10 rounded-full blur-2xl" />
        <div className="absolute bottom-20 right-20 w-32 h-32 bg-brand-500/10 rounded-full blur-3xl" />

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
          {/* Breadcrumb */}
          <div className="flex items-center justify-center gap-2 text-sm text-brand-600 mb-8">
            <Link to="/" className="hover:text-brand-700 transition-colors">Home</Link>
            <svg className="w-4 h-4 text-surface-800" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <span className="text-surface-800">Meet Mac</span>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Text Content */}
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand-100 rounded-full text-brand-700 text-sm font-medium mb-6">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
                Your claims assistant
              </div>

              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-brand-950 leading-tight mb-6">
                Meet{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-600 to-brand-800">
                  Mac
                </span>
              </h1>
              
              <p className="text-lg sm:text-xl text-surface-800 max-w-xl mx-auto lg:mx-0 mb-6">
                I help you review evidence, identify missing information, and prepare a clear claim pack. You review the documents, submit them, and manage the claim yourself.
              </p>

              <p className="text-surface-600 max-w-xl mx-auto lg:mx-0 mb-6">
                I don't promise outcomes. I help you put together complete, clearly organised documents using the evidence you provide.
              </p>

              {/* Inline YMYL disclaimer — kept close to the introduction */}
              <p className="text-sm text-surface-500 max-w-xl mx-auto lg:mx-0 border-l-2 border-surface-300 pl-3">
                Mac is an AI document-preparation assistant, not a solicitor or law firm. It analyses
                evidence and formats documents — it doesn't provide legal advice.{' '}
                <a href="#straight-talk" className="text-brand-600 hover:text-brand-700 font-medium">
                  Read the full picture
                </a>
              </p>
            </div>

            {/* Mac Avatar */}
            <div className="flex justify-center lg:justify-end">
              <div className="relative">
                {/* Glow effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-brand-400/20 to-accent-400/20 rounded-full blur-3xl scale-110" />
                
                {/* Avatar container */}
                <div className="relative w-72 h-72 sm:w-80 sm:h-80 lg:w-96 lg:h-96 rounded-full overflow-hidden border-4 border-white shadow-2xl shadow-brand-900/20">
                  <img 
                    src="/mac-avatar.png" 
                    alt="Mac - Your Fixtyer claims assistant"
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Floating badge */}
                <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-white px-4 py-2 rounded-full shadow-lg border border-surface-200">
                  <span className="text-brand-700 font-semibold text-sm">Claims Assistant</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom diagonal stripe bar */}
        <div 
          className="absolute bottom-0 left-0 right-0 h-8"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`
          }}
        />
      </section>

      {/* What Mac Does */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-accent-500 text-sm font-semibold tracking-widest uppercase">
              WHAT I DO
            </span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-brand-950 mt-3 mb-4">
              Here's how I help
            </h2>
            <p className="text-surface-800 text-lg max-w-2xl mx-auto">
              At each preparation step, I help organise the information and evidence you provide.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {capabilities.map((capability, index) => (
              <div 
                key={index}
                className="group p-6 bg-gradient-to-br from-brand-50 to-white rounded-2xl border-2 border-brand-100 hover:border-brand-300 transition-all duration-300 hover:shadow-xl hover:shadow-brand-900/5"
              >
                <div className="w-12 h-12 bg-brand-100 rounded-xl flex items-center justify-center text-brand-600 mb-4 group-hover:bg-brand-600 group-hover:text-white transition-colors duration-300">
                  {capability.icon}
                </div>
                <h3 className="font-display text-lg font-semibold text-brand-950 mb-2">
                  {capability.title}
                </h3>
                <p className="text-surface-700 text-sm leading-relaxed">
                  {capability.description}
                </p>
              </div>
            ))}
          </div>

          <p className="text-center text-surface-700 mt-10">
            Curious what all of this produces?{' '}
            <Link to="/pricing" className="text-brand-600 hover:text-brand-700 font-medium underline decoration-brand-300 underline-offset-2">
              See exactly what's inside Mac's claim packs
            </Link>
          </p>
        </div>
      </section>

      {/* How Mac Works */}
      <section className="py-20 bg-brand-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-accent-500 text-sm font-semibold tracking-widest uppercase">
              HOW I WORK
            </span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-white mt-3 mb-4">
              No magic, just a clear process
            </h2>
            <p className="text-brand-300 text-lg max-w-2xl mx-auto">
              Here's what that means for you.
            </p>
          </div>

          <div className="space-y-6">
            {howItWorks.map((item, index) => (
              <div 
                key={index}
                className="flex items-start gap-6 p-6 bg-brand-900/50 rounded-2xl border border-brand-800"
              >
                <div className="flex-shrink-0 w-12 h-12 bg-accent-500/20 rounded-xl flex items-center justify-center">
                  <span className="text-accent-400 font-mono font-bold">{String(index + 1).padStart(2, '0')}</span>
                </div>
                <div>
                  <h4 className="font-display text-lg font-semibold text-white mb-2">
                    {item.title}
                  </h4>
                  <p className="text-brand-300 leading-relaxed">
                    {item.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Quote */}
          <div className="mt-12 p-8 bg-accent-500/10 border border-accent-500/30 rounded-2xl">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-full overflow-hidden border-2 border-accent-500/50">
                <img 
                  src="/mac-avatar.png" 
                  alt="Mac"
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <p className="text-white text-lg italic leading-relaxed mb-3">
                  "I'll be honest with you. If something's missing from your evidence, I'll flag it. If your documents need work, I'll tell you. My job is to make sure what you submit is complete, clear, and professionally presented."
                </p>
                <p className="text-accent-400 font-medium">— Mac</p>
              </div>
            </div>
          </div>

          <p className="text-center text-brand-300 mt-8">
            Want the legal detail behind this?{' '}
            <Link to="/guides/section-58-defence" className="text-accent-400 hover:text-accent-300 font-medium underline decoration-accent-500/50 underline-offset-2">
              Read how the Section 58 defence works
            </Link>
            {' '}— and how Mac helps you counter it.
          </p>
        </div>
      </section>

      {/* Important Information */}
      <section id="straight-talk" className="py-20 bg-gradient-to-br from-brand-50 via-white to-accent-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl border-2 border-brand-200 p-8 sm:p-10 shadow-lg">
            <div className="flex items-start gap-4 mb-6">
              <div className="flex-shrink-0 w-12 h-12 rounded-full overflow-hidden border-2 border-brand-200">
                <img 
                  src="/mac-avatar.png" 
                  alt="Mac"
                  className="w-full h-full object-cover"
                />
              </div>
              <h2 className="font-display text-2xl sm:text-3xl font-bold text-brand-950 pt-2">
                Let me be straight with you
              </h2>
            </div>

            <div className="space-y-4 text-surface-700 leading-relaxed">
              <p>
                I'm an AI — not a person, not a solicitor. I don't give legal advice and I can't guarantee your claim will succeed. That's the council's call, not mine.
              </p>
              <p>
                What I do is help you prepare complete, clearly organised documents with the evidence you have. You submit and manage the claim yourself — Fixtyer doesn't do that part.
              </p>
              <p>
                If your case is complex or you end up in a legal dispute, get proper legal advice. I'm good at what I do, but I'm not a substitute for a solicitor.
              </p>
            </div>

            <div className="mt-8 pt-6 border-t border-brand-100">
              <p className="text-surface-600 text-sm text-center">
                Questions? Check our{' '}
                <Link to="/faq" className="text-brand-600 hover:text-brand-700 font-medium">
                  FAQ
                </Link>
                {' '}or{' '}
                <Link to="/contact" className="text-brand-600 hover:text-brand-700 font-medium">
                  get in touch
                </Link>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-accent-500 text-sm font-semibold tracking-widest uppercase">
              COMMON QUESTIONS
            </span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-brand-950 mt-3 mb-4">
              Questions about Mac
            </h2>
            <p className="text-surface-800 text-lg max-w-2xl mx-auto">
              Straight answers about what Mac is, what it does, and where its limits are.
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq) => (
              <div
                key={faq.question}
                className="p-6 bg-gradient-to-br from-brand-50 to-white rounded-2xl border-2 border-brand-100"
              >
                <h3 className="font-display text-lg font-semibold text-brand-950 mb-2">
                  {faq.question}
                </h3>
                <p className="text-surface-700 leading-relaxed">
                  {faq.answer}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-brand-600 to-brand-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 rounded-full overflow-hidden border-4 border-white/20">
              <img 
                src="/mac-avatar.png" 
                alt="Mac"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
          
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
            Ready to get started?
          </h2>
          <p className="text-xl text-brand-100 mb-8 max-w-2xl mx-auto">
            I'll help you put together your claim. The first step is free — let's see what you've got.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check"
              className="inline-flex items-center justify-center px-8 py-4 bg-white text-brand-700 font-semibold rounded-xl hover:bg-brand-50 transition-all hover:shadow-lg"
            >
              Let Mac Check Your Eligibility — Free
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/how-it-works"
              className="inline-flex items-center justify-center px-8 py-4 bg-transparent text-white font-semibold rounded-xl border-2 border-white/30 hover:bg-white/10 transition-colors"
            >
              See How It Works
            </Link>
          </div>

          <p className="text-brand-200 text-sm mt-6">
            Not sure you have a case yet?{' '}
            <Link to="/guides/what-makes-a-valid-claim" className="text-white hover:text-brand-100 font-medium underline decoration-white/40 underline-offset-2">
              Learn what makes a valid pothole claim
            </Link>
          </p>

          {/* Trust indicators */}
          <div className="mt-12 flex flex-wrap justify-center gap-8 text-brand-100 text-sm">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-success-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Free eligibility check
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-success-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              No hidden fees
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-success-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Keep 100% of compensation
            </div>
          </div>
        </div>
      </section>

      {/* Bottom diagonal stripe bar */}
      <div 
        className="h-8"
        style={{
          background: `repeating-linear-gradient(
            -45deg,
            #1e40af,
            #1e40af 40px,
            #f97316 40px,
            #f97316 80px
          )`
        }}
      />
    </div>
  );
}
